import React, { useState, useRef, useEffect } from 'react';
import { ViewType } from '../types';
import { Gamepad2, ChevronDown } from 'lucide-react';

interface HeaderProps {
  currentView: ViewType;
  onNavigate: (view: ViewType) => void;
  onScrollToGame?: (game: 'tower' | 'draw' | 'puzzle') => void;
}

export const Header: React.FC<HeaderProps> = ({
  currentView,
  onNavigate,
  onScrollToGame,
}) => {
  const [isGamesDropdownOpen, setIsGamesDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Detect scroll position to elevate header background
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsGamesDropdownOpen(false);
      }
    };
    if (isGamesDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isGamesDropdownOpen]);

  const handleSelectGame = (game: 'tower' | 'draw' | 'puzzle') => {
    setIsGamesDropdownOpen(false);
    if (onScrollToGame) {
      onScrollToGame(game);
    } else {
      const targetId = `game-${game}`;
      if (currentView !== 'home') {
        onNavigate('home');
        setTimeout(() => {
          const el = document.getElementById(targetId);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            el.classList.add('ring-2', 'ring-[#ffd700]');
            setTimeout(() => el.classList.remove('ring-2', 'ring-[#ffd700]'), 1800);
          }
        }, 150);
      } else {
        const el = document.getElementById(targetId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          el.classList.add('ring-2', 'ring-[#ffd700]');
          setTimeout(() => el.classList.remove('ring-2', 'ring-[#ffd700]'), 1800);
        }
      }
    }
  };

  return (
    <header
      className={`world-header fixed top-0 left-0 right-0 z-40 w-full transition-all duration-300 ${
        currentView !== 'home' || isScrolled
          ? 'topbar-v2 bg-[#1b1117]/95 backdrop-blur-md shadow-[0_8px_32px_rgba(0,0,0,0.65)] border-b border-[#e6ba55]/40'
          : 'bg-gradient-to-b from-[#170d13]/90 via-[#170d13]/60 to-transparent border-b border-[#e6ba55]/25 backdrop-blur-[2px]'
      }`}
    >
      {/* Brand logo */}
      <button
        type="button"
        onClick={() => {
          if (currentView === 'home') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } else {
            onNavigate('home');
          }
        }}
        className="brand text-left shrink-0"
        aria-label="VNFAM 2.0 — Trang chủ"
      >
        <img
          src="/images/logo/vnfam-logo-horizontal-transparent.webp"
          alt="Bảo tàng Mỹ thuật Việt Nam"
          width="142"
          className="h-auto"
        />
        <span className="hidden sm:inline">PLAY THE ART</span>
      </button>

      {/* Center Navigation Links (desktop) */}
      <nav aria-label="Điều hướng" className="hidden md:flex items-center gap-6 ml-auto mr-4">
        <button
          type="button"
          onClick={() => {
            if (currentView === 'home') {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
              onNavigate('home');
            }
          }}
          className={currentView === 'home' ? 'active' : ''}
          aria-current={currentView === 'home' ? 'page' : undefined}
        >
          Nhập cuộc
        </button>

        <button
          type="button"
          onClick={() => onNavigate('journey')}
          className={currentView === 'journey' ? 'active' : ''}
          aria-current={currentView === 'journey' ? 'page' : undefined}
        >
          Bộ sưu tập
        </button>

        <button
          type="button"
          onClick={() => onNavigate('summary')}
          className={currentView === 'summary' ? 'active' : ''}
          aria-current={currentView === 'summary' ? 'page' : undefined}
        >
          Dấu ấn của bạn
        </button>
      </nav>

      {/* QUICK GAME SELECTOR: Desktop segmented buttons + Mobile compact dropdown */}
      <div className="flex items-center gap-2 ml-auto md:ml-0" ref={dropdownRef}>
        {/* Desktop Quick Game Buttons */}
        <div className="hidden lg:flex items-center gap-1 bg-black/40 border border-[#e6ba55]/40 rounded-full px-2 py-1 shadow-inner backdrop-blur-sm">
          <span className="text-[10px] font-bold text-[#fef08a] px-1 uppercase tracking-wider flex items-center gap-1">
            <Gamepad2 className="w-3 h-3 text-[#f59e0b]" />
            Game:
          </span>
          <button
            type="button"
            onClick={() => handleSelectGame('tower')}
            className="px-2.5 py-0.5 rounded-full text-xs font-bold text-[#fff4dd] hover:text-[#ffd700] hover:bg-[#e6ba55]/20 transition-all flex items-center gap-1 active:scale-95"
            title="Cuộn nhanh tới Leo Tháp Mỹ Thuật"
          >
            <span>🏰</span>
            <span>Leo tháp</span>
          </button>
          <button
            type="button"
            onClick={() => handleSelectGame('draw')}
            className="px-2.5 py-0.5 rounded-full text-xs font-bold text-[#fff4dd] hover:text-[#ffd700] hover:bg-[#e6ba55]/20 transition-all flex items-center gap-1 active:scale-95"
            title="Cuộn nhanh tới Vẽ & Đoán Tác Phẩm"
          >
            <span>🎨</span>
            <span>Vẽ & đoán</span>
          </button>
          <button
            type="button"
            onClick={() => handleSelectGame('puzzle')}
            className="px-2.5 py-0.5 rounded-full text-xs font-bold text-[#fff4dd] hover:text-[#ffd700] hover:bg-[#e6ba55]/20 transition-all flex items-center gap-1 active:scale-95"
            title="Cuộn nhanh tới Ghép Tranh Di Sản"
          >
            <span>🧩</span>
            <span>Ghép tranh</span>
          </button>
        </div>

        {/* Mobile & Compact Screen Dropdown Button */}
        <div className="relative lg:hidden">
          <button
            type="button"
            onClick={() => setIsGamesDropdownOpen((prev) => !prev)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-[#8b152d]/90 to-[#4a0d1e]/90 hover:from-[#a51936] hover:to-[#5e1227] text-[#fef08a] border border-[#e6ba55]/70 shadow-md text-xs font-bold active:scale-95 transition-all"
            aria-expanded={isGamesDropdownOpen}
            aria-label="Chọn nhanh trò chơi để cuộn xuống dưới"
          >
            <Gamepad2 className="w-3.5 h-3.5 text-[#fbbf24]" />
            <span className="text-[11px] font-black">3 Game</span>
            <ChevronDown className={`w-3 h-3 text-[#fef08a] transition-transform ${isGamesDropdownOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Quick Dropdown Menu */}
          {isGamesDropdownOpen && (
            <div className="absolute right-0 top-full mt-2 w-64 bg-[#231419]/95 backdrop-blur-xl border border-[#e6ba55]/60 rounded-2xl p-2 shadow-2xl z-50 animate-fade-in space-y-1">
              <div className="text-[9px] font-bold text-[#f5d0c5]/60 uppercase tracking-wider px-2 py-1 flex items-center justify-between border-b border-[#e6ba55]/20 pb-1.5">
                <span>Chọn trò chơi</span>
                <span className="text-[#fbbf24]">Cuộn xuống ↓</span>
              </div>

              <button
                type="button"
                onClick={() => handleSelectGame('tower')}
                className="w-full text-left p-2 rounded-xl hover:bg-[#3d182f] flex items-center gap-2.5 text-xs text-[#fff4dd] transition-all group active:scale-98"
              >
                <span className="w-8 h-8 rounded-lg bg-[#451829] border border-[#e6ba55]/40 flex items-center justify-center text-base group-hover:scale-110 transition-transform shrink-0">
                  🏰
                </span>
                <div className="min-w-0">
                  <div className="font-bold text-[#fef08a] group-hover:text-[#ffd700]">01 · Leo tháp mỹ thuật</div>
                  <div className="text-[10px] text-[#f5d0c5]/70 truncate">10 tầng câu đố & tri thức</div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => handleSelectGame('draw')}
                className="w-full text-left p-2 rounded-xl hover:bg-[#1a3832]/60 flex items-center gap-2.5 text-xs text-[#fff4dd] transition-all group active:scale-98"
              >
                <span className="w-8 h-8 rounded-lg bg-[#1a3832] border border-[#34d399]/40 flex items-center justify-center text-base group-hover:scale-110 transition-transform shrink-0">
                  🎨
                </span>
                <div className="min-w-0">
                  <div className="font-bold text-[#a7f3d0] group-hover:text-[#6ee7b7]">02 · Vẽ & đoán tác phẩm</div>
                  <div className="text-[10px] text-[#f5d0c5]/70 truncate">Bảng màu sơn mài cổ truyền</div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => handleSelectGame('puzzle')}
                className="w-full text-left p-2 rounded-xl hover:bg-[#422c16]/60 flex items-center gap-2.5 text-xs text-[#fff4dd] transition-all group active:scale-98"
              >
                <span className="w-8 h-8 rounded-lg bg-[#3d2a14] border border-[#fbbf24]/40 flex items-center justify-center text-base group-hover:scale-110 transition-transform shrink-0">
                  🧩
                </span>
                <div className="min-w-0">
                  <div className="font-bold text-[#fde68a] group-hover:text-[#fef08a]">03 · Ghép tranh di sản</div>
                  <div className="text-[10px] text-[#f5d0c5]/70 truncate">6 Bảo vật Quốc gia vô giá</div>
                </div>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
